<script>
import APICalendarEvents from '@school/services/API/CalendarEvents';
import CalendarEventDetailModal from '@school/components/CalendarEventDetailModal.vue';
import CalendarItemCard from '@school/components/CalendarItemCard.vue';
import CalendarGrid from '@shared/components/CalendarGrid.vue';
import { expandRecurringCalendarEvents } from '@shared/utils/calendar';

const LIST_LIMIT = 20;

export default {
  name: 'Calendar',
  components: { CalendarGrid, CalendarItemCard },
  data() {
    const now = this.$moment();
    return {
      items: [],
      isFetching: false,
      viewMode: 'month',
      currentMonth: now.month(),
      currentYear: now.year(),
      selectedDay: null,
      selectedDayEvents: [],
      LIST_LIMIT,
    };
  },
  computed: {
    upcomingItems() {
      const now = this.$moment();
      const rangeStart = now.format('YYYY-MM-DD');
      const rangeEnd = now.clone().add(6, 'months').format('YYYY-MM-DD');
      const expanded = expandRecurringCalendarEvents(this.items, rangeStart, rangeEnd);
      return expanded
        .filter((e) => this.$moment.utc(e.start_date).isSameOrAfter(now, 'day'))
        .sort((a, b) => this.$moment.utc(a.start_date).diff(this.$moment.utc(b.start_date)))
        .slice(0, LIST_LIMIT);
    },
  },
  created() {
    // The calendar can be reached by URL even when the store disabled it
    const options = this.$store.state.store.data.calendar_options;

    if (!options || !options.enabled) {
      this.$router.replace({ name: 'dashboard' });
      return;
    }

    this.fetchCalendar();
  },
  methods: {
    async fetchCalendar() {
      this.isFetching = true;
      try {
        const { data } = await APICalendarEvents.getCalendar();
        this.items = data;
      } catch (e) {
        // silent
      } finally {
        this.isFetching = false;
      }
    },
    onMonthChange({ month, year }) {
      this.currentMonth = month;
      this.currentYear = year;
      this.selectedDay = null;
      this.selectedDayEvents = [];
    },
    onDayClick(cell) {
      this.selectedDay = cell.dateStr;
      this.selectedDayEvents = cell.events;
    },
    openItemDetail(item) {
      this.$buefy.modal.open({
        parent: this,
        component: CalendarEventDetailModal,
        hasModalCard: true,
        trapFocus: true,
        props: { event: item },
      });
    },
    formatDate(date) {
      return this.$moment(date).format('dddd DD MMMM YYYY');
    },
  },
  i18n: {
    messages: {
      en: {
        calendar_title: 'Calendar',
        calendar_description: 'View upcoming events and sessions.',
        view_month: 'Month',
        view_list: 'List',
        no_events: 'No upcoming events.',
        no_events_day: 'No events on this day.',
        upcoming_events: 'Upcoming events',
        list_limit: 'Only the next {count} events are shown here. Check the calendar view to see all events.',
      },
      fr: {
        calendar_title: 'Calendrier',
        calendar_description: 'Consultez ici vos prochains événements.',
        view_month: 'Vue calendrier',
        view_list: 'Vue liste',
        no_events: 'Aucun événement à venir.',
        no_events_day: 'Aucun événement ce jour.',
        upcoming_events: 'Événements à venir',
        list_limit: 'Seuls les {count} prochains événements sont affichés ici. Consultez la vue calendrier pour voir tous les événements.',
      },
      es: {
        calendar_title: 'Calendario',
        calendar_description: 'Consulta los próximos eventos y sesiones.',
        view_month: 'Mes',
        view_list: 'Lista',
        no_events: 'No hay eventos próximos.',
        no_events_day: 'No hay eventos este día.',
        upcoming_events: 'Próximos eventos',
        list_limit: 'Solo se muestran aquí los próximos {count} eventos. Consulta la vista de calendario para ver todos los eventos.',
      },
    },
  },
};
</script>

<template>
  <div class="page px-5 py-10">
    <h1 class="title is-custom has-text-centered is-size-6-touch">
      {{ $t('calendar_title') }}
    </h1>
    <div class="content has-text-centered mb-5">
      <p>{{ $t('calendar_description') }}</p>
    </div>

    <!-- View toggle -->
    <div class="is-flex is-justify-content-center mb-5">
      <b-field class="mb-0">
        <b-radio-button v-model="viewMode" native-value="month" type="is-primary is-custom">
          <b-icon icon="calendar" size="is-small" />
          <span>{{ $t('view_month') }}</span>
        </b-radio-button>
        <b-radio-button v-model="viewMode" native-value="list" type="is-primary is-custom">
          <b-icon icon="list" size="is-small" />
          <span>{{ $t('view_list') }}</span>
        </b-radio-button>
      </b-field>
    </div>

    <!-- Monthly view (default) -->
    <template v-if="viewMode === 'month'">
      <CalendarGrid
        :events="items"
        :month="currentMonth"
        :year="currentYear"
        :selected-day="selectedDay"
        @month-change="onMonthChange"
        @event-click="openItemDetail"
        @day-click="onDayClick"
      />

      <!-- Selected day events -->
      <div v-if="selectedDay" class="mt-5">
        <h2 class="title is-6 is-capitalized">
          {{ formatDate(selectedDay) }}
        </h2>
        <div v-if="selectedDayEvents.length === 0" class="has-text-grey-dark is-size-7">
          {{ $t('no_events_day') }}
        </div>
        <CalendarItemCard
          v-for="item in selectedDayEvents"
          :key="`${item.uuid}-${item.start_date}`"
          :item="item"
          @click="openItemDetail"
        />
      </div>
    </template>

    <!-- List view -->
    <template v-if="viewMode === 'list'">
      <h2 class="title is-6 mb-2">
        {{ $t('upcoming_events') }}
      </h2>
      <p class="has-text-grey-dark is-size-8 mb-4">
        {{ $t('list_limit', { count: LIST_LIMIT }) }}
      </p>
      <div v-if="isFetching" class="has-text-centered py-5">
        <b-skeleton height="60" class="mb-3" />
        <b-skeleton height="60" class="mb-3" />
      </div>
      <div v-else-if="upcomingItems.length === 0" class="has-text-centered has-text-grey-dark py-5">
        {{ $t('no_events') }}
      </div>
      <template v-else>
        <CalendarItemCard
          v-for="item in upcomingItems"
          :key="`${item.uuid}-${item.start_date}`"
          :item="item"
          @click="openItemDetail"
        />
      </template>
    </template>
  </div>
</template>
